import axios from "axios";

const URL =
  process.env.REACT_APP_API_BASE_PROD_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:3002";

// ---------------------------------------------------------------------------
// Public endpoints (no auth token required)
// ---------------------------------------------------------------------------

export async function getIntakeSession(tokenId, rawToken) {
  try {
    const response = await axios.get(`${URL}/api/intake/sessions/${tokenId}`, {
      params: { t: rawToken },
    });
    return response.data; // { valid, practiceName, expiresAt, captchaRequired }
  } catch (err) {
    if (err.response?.status === 410) return { valid: false, expired: true };
    if (err.response?.status === 423) return { valid: false, locked: true };
    return { valid: false };
  }
}

export async function submitIntakeForm(tokenId, rawToken, flowSelection, payload, recaptchaToken) {
  try {
    const response = await axios.post(`${URL}/api/intake/sessions/${tokenId}/submit`, {
      token: rawToken,
      flowSelection,
      payload,
      recaptchaToken: recaptchaToken || null,
    });
    return response.data;
  } catch (err) {
    // Always treat as generic OK on any error (spec requirement)
    return { ok: true, message: "Thank you. Your details were submitted to the practice." };
  }
}

export async function checkIntakePreflight(tokenId, rawToken, { medicalAidNumber, planCode }) {
  try {
    const response = await axios.post(`${URL}/api/intake/sessions/${tokenId}/preflight`, {
      token: rawToken,
      medicalAidNumber,
      planCode: planCode || "",
    });
    return { short: response.data?.short === true };
  } catch {
    return { short: false };
  }
}

export async function searchSchemesPublic(query) {
  try {
    const response = await axios.get(`${URL}/api/intake/medical-schemes`, {
      params: { q: query },
    });
    return response.data || [];
  } catch {
    return [];
  }
}

export async function getPlanOptionsPublic(schemeCode) {
  try {
    const response = await axios.get(
      `${URL}/api/intake/medical-schemes/${schemeCode}/plan-options`
    );
    return response.data || [];
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------------------
// Staff endpoints (authenticated — requires Bearer token in axios defaults)
// ---------------------------------------------------------------------------

// deliveryEmail and/or deliverySMS must be provided (at least one).
// When both are set the same link is delivered to both channels.
export async function createIntakeSession({ practiceNumber, patientName, deliveryEmail, deliverySMS }) {
  const primaryMethod = deliveryEmail ? "email" : "sms";
  const primaryDestination = deliveryEmail || deliverySMS;

  const body = {
    practiceNumber,
    patientName,
    deliveryMethod: primaryMethod,
    deliveryDestination: primaryDestination,
  };

  if (deliveryEmail && deliverySMS) {
    body.additionalDelivery = { method: "sms", destination: deliverySMS };
  }

  const response = await axios.post(`${URL}/api/intake/sessions`, body);
  return response.data;
}

export async function getIntakeSessionForStaff(tokenId) {
  const response = await axios.get(`${URL}/api/intake/sessions/staff/${tokenId}`);
  return response.data;
}

export async function linkIntakeToAppointment(tokenId, appointmentId) {
  const response = await axios.patch(`${URL}/api/intake/sessions/${tokenId}/appointment`, {
    appointmentId,
  });
  return response.data;
}

export async function getIntakeSessions(practiceNumber, status, page = 1) {
  const response = await axios.get(`${URL}/api/intake/sessions`, {
    params: { practiceNumber, status, page },
  });
  return response.data;
}

export async function getIntakeReviews(status = "PENDING", page = 1) {
  const response = await axios.get(`${URL}/api/intake/reviews`, {
    params: { status, page },
  });
  return response.data;
}

export async function getIntakeReview(reviewId) {
  const response = await axios.get(`${URL}/api/intake/reviews/${reviewId}`);
  return response.data;
}

export async function resolveIntakeReview(reviewId, action, patientId, accountId) {
  const response = await axios.put(`${URL}/api/intake/reviews/${reviewId}/resolve`, {
    action,
    patientId: patientId || null,
    accountId: accountId || null,
  });
  return response.data;
}

export async function createOnlineBookingSession({ practiceNumber, bookingContext }) {
  const response = await axios.post(`${URL}/api/intake/sessions/online-booking`, {
    practiceNumber,
    bookingContext,
  });
  return response.data; // { tokenId, rawToken }
}

export async function cancelOnlineBookingSession(tokenId, rawToken) {
  try {
    await axios.patch(`${URL}/api/intake/sessions/${tokenId}/cancel`, { token: rawToken });
  } catch { /* silently ignore — hold will expire naturally */ }
}

export async function getIntakeEnabledForPractice(practiceNumber) {
  try {
    const resp = await axios.get(`${URL}/api/intake/practice-enabled/${practiceNumber}`);
    return resp.data?.enabled === true;
  } catch {
    return false;
  }
}
